import type { GetAccountToken } from './accountAuthToken';

const DEFAULT_DEBOUNCE_MS = 1_500;

type AccountDataUploader = (token: string) => Promise<void>;

let getAccountToken: GetAccountToken | undefined;
let uploader: AccountDataUploader = uploadCurrentAccountData;
let debounceMs = DEFAULT_DEBOUNCE_MS;
let timer: ReturnType<typeof setTimeout> | undefined;
let pending = false;
let flushing: Promise<void> | undefined;

async function uploadCurrentAccountData(token: string): Promise<void> {
  const [
    { getAccountSyncKey },
    { snapshotAccountDataBundle },
    { downloadAccountDataBundle, uploadAccountDataBundle },
    { mergeAccountDataBundleForAutoUpload },
  ] =
    await Promise.all([
      import('./syncKeyStore.ts'),
      import('./localAccountData.ts'),
      import('./accountDataSyncApi.ts'),
      import('./accountSourceAuthBundle.ts'),
    ]);
  const [syncKey, bundle] = await Promise.all([
    getAccountSyncKey(token),
    snapshotAccountDataBundle(),
  ]);
  const existingBundle = await downloadAccountDataBundle(token, syncKey);
  await uploadAccountDataBundle(
    token,
    mergeAccountDataBundleForAutoUpload(bundle, existingBundle),
    syncKey,
  );
}

function shouldLogAutoSyncFailures(): boolean {
  return (
    typeof import.meta.env === 'object' &&
    import.meta.env !== null &&
    import.meta.env.DEV === true
  );
}

/**
 * Register the current account-token provider. Mutating features can call
 * `requestAccountDataAutoSync()` without knowing whether Clerk is enabled.
 */
export function configureAccountDataAutoSync(provider: GetAccountToken | undefined): void {
  getAccountToken = provider;
}

/**
 * Best-effort account backup for local account-owned data. Calls are debounced
 * so fast reader progress updates collapse into a single encrypted bundle PUT.
 */
export function requestAccountDataAutoSync(): void {
  pending = true;
  if (timer !== undefined) {
    clearTimeout(timer);
  }
  timer = setTimeout(() => {
    timer = undefined;
    void flushAccountDataAutoSync();
  }, debounceMs);
}

export async function flushAccountDataAutoSync(): Promise<void> {
  if (timer !== undefined) {
    clearTimeout(timer);
    timer = undefined;
  }
  if (flushing) {
    pending = true;
    await flushing;
    if (pending) {
      await flushAccountDataAutoSync();
    }
    return;
  }
  if (!pending) return;

  pending = false;
  const provider = getAccountToken;
  if (!provider) return;

  flushing = (async () => {
    const token = await provider();
    if (!token) return;
    await uploader(token);
  })();

  try {
    await flushing;
  } catch (err) {
    if (shouldLogAutoSyncFailures()) {
      console.warn('[account] auto-sync failed', err);
    }
  } finally {
    flushing = undefined;
  }

  if (pending) {
    await flushAccountDataAutoSync();
  }
}

export function setAccountDataAutoSyncUploaderForTest(next: AccountDataUploader): void {
  uploader = next;
}

export function setAccountDataAutoSyncDebounceForTest(nextMs: number): void {
  debounceMs = nextMs;
}

export function resetAccountDataAutoSyncForTest(): void {
  if (timer !== undefined) {
    clearTimeout(timer);
    timer = undefined;
  }
  getAccountToken = undefined;
  uploader = uploadCurrentAccountData;
  debounceMs = DEFAULT_DEBOUNCE_MS;
  pending = false;
  flushing = undefined;
}
